import { LocationData } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { buildChartOptions } from '@configurations/campaignAnalytics/stateVisitsChart';
import { Grid, Stack } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo } from 'react';
import { ChartCard } from '../ChartCard';
import { EditableChartProps } from './chartProps';
import LocationTable, { LocationTableProps } from '../LocationTable';

type StateVisitsProps = EditableChartProps;

const STATES_COUNT = 20;

type TableColumn = keyof LocationData | 'rank';

type ColumnData = {
  column: TableColumn;
  label: string;
};

const ORDERED_COLUMNS: ColumnData[] = [
  { column: 'rank', label: 'Rank' },
  { column: 'location', label: 'State' },
  { column: 'visitIndex', label: 'Visit Index' },
];

const buildTableData = (
  locationData: LocationData[]
): LocationTableProps['tableData'] => [
  ORDERED_COLUMNS.map(columnData => ({
    key: columnData.column,
    value: columnData.label,
  })),
  ...locationData.map((stateLocation, i) =>
    ORDERED_COLUMNS.map(columnData => ({
      key: columnData.column,
      value:
        columnData.column === 'rank' ? i + 1 : stateLocation[columnData.column],
    }))
  ),
];

const StateVisits = (props: StateVisitsProps) => {
  const { report, ...chartCardProps } = props;

  const orderedStatesData = useMemo(() => {
    return report.stateVisits.data.toSorted(
      (a, b) => b.visitIndex - a.visitIndex
    );
  }, [report]);

  const memoizedChartOptions = useMemo(() => {
    return buildChartOptions(orderedStatesData);
  }, [orderedStatesData]);

  return (
    <ChartCard
      title="Top State Visits"
      description={`${props.campaignName} (${props.campaignId})`}
      report={report.stateVisits}
      {...chartCardProps}
    >
      <Grid container>
        <Grid size={6}>
          <Stack height="100%" justifyContent="center">
            <HighchartsReact
              constructorType="mapChart"
              highcharts={Highcharts}
              options={memoizedChartOptions}
            />
          </Stack>
        </Grid>
        <Grid size={6}>
          <LocationTable
            tableData={buildTableData(orderedStatesData.slice(0, STATES_COUNT))}
          />
        </Grid>
      </Grid>
    </ChartCard>
  );
};

export default StateVisits;
